import { createContext, useContext, useEffect, useMemo, useState } from 'react';

import { useMediaQuery } from '@ag1/ui';
import { MediaContextValue, MediaProviderProps } from './types';

const MediaContext = createContext<MediaContextValue>({
  isReady: false,
  atXSmall: false,
  atSmall: false,
  atSSMedium: false,
  atSMedium: false,
  atMedium: false,
  atLarge: false,
  atXLarge: false,
  atXXLarge: false
});

const MediaProvider = ({ children }: MediaProviderProps) => {
  const atXSmall = useMediaQuery('xs');
  const atSmall = useMediaQuery('s');
  const atSSMedium = useMediaQuery('ssm');
  const atSMedium = useMediaQuery('sm');
  const atMedium = useMediaQuery('m');
  const atLarge = useMediaQuery('l');
  const atXLarge = useMediaQuery('xl');
  const atXXLarge = useMediaQuery('xxl');
  const [isReady, setIsReady] = useState(false);

  const value = useMemo<MediaContextValue>(
    () => ({
      atXSmall,
      atSmall,
      atSSMedium,
      atSMedium,
      atMedium,
      atLarge,
      atXLarge,
      atXXLarge,
      isReady
    }),
    [atXSmall, atSmall, atSSMedium, atSMedium, atMedium, atLarge, atXLarge, atXXLarge, isReady]
  );

  useEffect(() => {
    // matchMedia is not available in SSR
    setIsReady(true);
  }, []);

  return <MediaContext.Provider value={value}>{children}</MediaContext.Provider>;
};

function useMedia() {
  const context = useContext(MediaContext);

  if (context === undefined) {
    throw new Error('useMedia must be used within a MediaProvider');
  }

  return context;
}

export { MediaProvider, useMedia };
